import { CancelRounded, VisibilityRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack } from "@mui/material";
import { useState } from "react";
import {
  DatagridConfigurable,
  List,
  TextField,
  TopToolbar,
  SelectColumnsButton,
  CreateButton,
  SearchInput,
  SelectInput,
  DateField,
  required,
  FunctionField,
  useSidebarState,
  BooleanField,
} from "react-admin";

import { ListNoResultsWithFilters } from "@/components/ListNoResultsWithFilters";
import { ListPaginationByLimit } from "@/components/pagination";
import {
  CLIENT_SEARCH_SOURCE,
  FILTER_LIMIT_OPTIONS,
  FILTER_LIMITS,
} from "@/constants/filters";
import { OptionalBooleanInput } from "@/inputs/OptionalBooleanInput";

const ListActions = () => (
  <TopToolbar>
    <SelectColumnsButton />
    <CreateButton />
  </TopToolbar>
);

const VpnServersField = ({ vpnServers }: { vpnServers: string[] }) => {
  const [opened, setOpened] = useState(false);

  return (
    <Stack direction="column" alignItems="center" spacing={1}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setOpened((prev) => !prev);
        }}
      >
        {opened ? <CancelRounded /> : <VisibilityRounded />}
      </IconButton>
      {opened && (
        <Box sx={{ textAlign: "center" }}>
          {vpnServers.map((value) => value.split("/").pop()).join(", ")}
        </Box>
      )}
    </Stack>
  );
};

const DEFAULT_FILTERS = {
  limit: FILTER_LIMITS[0],
  deprecated: "false",
};

export const ClientAppsList = () => {
  const [open] = useSidebarState();
  return (
    <List
      actions={<ListActions />}
      filters={[
        <SearchInput
          key="search"
          source={CLIENT_SEARCH_SOURCE}
          placeholder="Search record by Page"
          alwaysOn
        />,
        <SelectInput
          key="limit"
          label="Limit"
          source="limit"
          choices={FILTER_LIMIT_OPTIONS}
          validate={required()}
          alwaysOn
        />,
        <OptionalBooleanInput key="deprecated" source="deprecated" alwaysOn />,
      ]}
      filterDefaultValues={DEFAULT_FILTERS}
      pagination={<ListPaginationByLimit />}
      storeKey={false}
    >
      <DatagridConfigurable
        bulkActionButtons={false}
        omit={["createdAt", "updatedAt"]}
        className={`datagrid-scroll ${!open ? "collapsed" : ""}`}
        empty={<ListNoResultsWithFilters defaultFilters={DEFAULT_FILTERS} />}
      >
        <DateField source="createdAt" showTime />
        <DateField source="updatedAt" showTime />
        <TextField source="title" />
        <TextField source="name" />
        <TextField source="email" />
        <TextField source="deviceTypeId" label="Device Type Id" />
        <FunctionField
          source="isCampaignDriven"
          render={({ isCampaignDriven }) => (
            <Chip
              label={isCampaignDriven ? "YES" : "NO"}
              color={isCampaignDriven ? "success" : "error"}
              size="small"
            />
          )}
        />
        <FunctionField
          source="vpnServers"
          render={(record: { vpnServers?: string[] }) => (
            <VpnServersField vpnServers={record.vpnServers ?? []} />
          )}
        />
        <TextField source="domain" />
        <FunctionField
          source="autoCreateHosts"
          render={({ autoCreateHosts }) => (
            <Chip
              label={autoCreateHosts ? "YES" : "NO"}
              color={autoCreateHosts ? "success" : "error"}
              size="small"
            />
          )}
        />
        <BooleanField source="deprecated" />
      </DatagridConfigurable>
    </List>
  );
};
